import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api, { CHURCH, LOGO_URL } from '@/api/client';
import Seo from '@/seo/Seo';
import { PAGE_SEO, BUSINESS } from '@/seo/seoConfig';
import { breadcrumbSchema, recurringServiceEvent } from '@/seo/schemas';
import { Youtube, Play, Radio, Calendar, Clock, ExternalLink, Users, Phone, Mail, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';

function VideoCard({ video, onClick, active, badge }) {
  return (
    <button onClick={onClick} className={`text-left card-moj overflow-hidden group ${active ? 'ring-2 ring-moj-gold' : ''}`} aria-label={`Play ${video.title}`}>
      <div className="relative aspect-video bg-moj-purple-deep">
        {video.thumbnail ? (
          <img src={video.thumbnail} alt={video.title} className="absolute inset-0 w-full h-full object-cover"/>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-moj-gold"><Youtube size={32}/></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex items-end p-3">
          {badge && <span className="live-badge text-[10px]">{badge}</span>}
        </div>
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition">
          <div className="w-14 h-14 rounded-full bg-moj-gold flex items-center justify-center"><Play size={24} fill="#2B0038" className="text-moj-purple-deep ml-1"/></div>
        </div>
      </div>
      <div className="p-3">
        <h4 className="font-serif text-sm text-moj-purple-deep line-clamp-2 mb-1">{video.title}</h4>
        <p className="text-xs text-moj-charcoal/60">{video.published_at ? new Date(video.published_at).toLocaleDateString() : ''}</p>
      </div>
    </button>
  );
}

export default function Live() {
  const [tab, setTab] = useState('live');
  const [selected, setSelected] = useState(null);
  const { data: yt, isLoading } = useQuery({ queryKey: ['live-yt'], queryFn: async () => (await api.get('/public/youtube/streams')).data });

  const featured = useMemo(() => {
    if (selected) return selected;
    if (!yt) return null;
    return yt.live?.[0] || yt.upcoming?.[0] || yt.completed?.[0] || null;
  }, [selected, yt]);

  const videos = yt ? (tab === 'live' ? yt.live : tab === 'upcoming' ? yt.upcoming : yt.completed) : [];
  const channel = yt?.channel;

  return (
    <div>
      <Seo
        title={PAGE_SEO.live.title}
        titleExact
        description={PAGE_SEO.live.description}
        keywords={PAGE_SEO.live.keywords}
        path="/live"
        jsonLd={[
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Watch LIVE', path: '/live' }]),
          recurringServiceEvent(BUSINESS.services.find((s) => s.name === 'Sunday Worship Service')),
        ]}
      />
      {/* Channel Banner */}
      <section className="gradient-purple text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <img src={LOGO_URL} alt="Ministry of Jesus logo" className="w-24 h-24 sm:w-32 sm:h-32 rounded-full bg-white p-2 shadow-2xl flex-shrink-0"/>
            <div className="flex-1">
              <h1 className="font-serif text-3xl sm:text-5xl text-white">{channel?.title || CHURCH.name}</h1>
              <p className="text-moj-gold/90 text-sm mt-1">{channel?.handle || `@${CHURCH.youtube.split('@').pop()}`}</p>
              <p className="text-white/80 text-sm mt-2 max-w-2xl line-clamp-2">{channel?.description || `A Spirit-filled Pentecostal church in Ireland.`}</p>
              <div className="flex flex-wrap items-center gap-4 mt-4">
                {channel?.subscriber_count != null && (
                  <span className="text-sm text-white/80"><Users className="inline mr-1" size={14}/> {channel.subscriber_count.toLocaleString()} subscribers</span>
                )}
                {channel?.video_count != null && (
                  <span className="text-sm text-white/80">{channel.video_count} videos</span>
                )}
              </div>
            </div>
            <a href={channel?.channel_url || CHURCH.youtube} target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid="visit-youtube">
              <Youtube size={18}/> Visit YouTube
            </a>
          </div>
        </div>
      </section>

      <section className="py-12 bg-moj-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-8">
          <div className="min-w-0">
            {/* Featured Player */}
            <div className="card-moj overflow-hidden mb-6">
              <div className="relative aspect-video bg-moj-purple-deep">
                {featured?.video_id ? (
                  <iframe
                    title={featured.title}
                    src={`https://www.youtube.com/embed/${featured.video_id}?autoplay=0&rel=0`}
                    className="absolute inset-0 w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center text-white">
                    <div className="text-center">
                      <Youtube size={56} className="mx-auto text-moj-gold mb-3"/>
                      <p>{isLoading ? 'Loading streams…' : 'No video available yet.'}</p>
                      <a href={`https://www.youtube.com/@${CHURCH.youtube.split('@').pop()}/streams`} target="_blank" rel="noopener noreferrer" className="btn-gold mt-4 inline-flex"><ExternalLink size={16}/> View on YouTube</a>
                    </div>
                  </div>
                )}
              </div>
              {featured && (
                <div className="p-5">
                  <h2 className="font-serif text-2xl text-moj-purple-deep mb-1">{featured.title}</h2>
                  <p className="text-sm text-moj-charcoal/70 line-clamp-3">{featured.description}</p>
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="flex gap-2 mb-4 flex-wrap" role="tablist">
              {[
                { id: 'live', label: 'LIVE Now', icon: Radio, count: yt?.live?.length || 0 },
                { id: 'upcoming', label: 'Upcoming', icon: Calendar, count: yt?.upcoming?.length || 0 },
                { id: 'completed', label: 'Previous Services', icon: Clock, count: yt?.completed?.length || 0 },
              ].map((t) => (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={tab === t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition ${tab === t.id ? 'bg-moj-purple text-white' : 'bg-white text-moj-purple-deep hover:bg-moj-cream-soft border border-moj-gold/30'}`}
                  data-testid={`yt-tab-${t.id}`}
                >
                  <t.icon size={16}/> {t.label}
                  {t.count > 0 && <span className="text-xs px-2 py-0.5 rounded-full bg-moj-gold/30">{t.count}</span>}
                </button>
              ))}
            </div>

            {/* Video Grid */}
            {videos && videos.length > 0 ? (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {videos.map((v) => (
                  <VideoCard key={v.video_id} video={v} onClick={() => setSelected(v)} active={selected?.video_id === v.video_id} badge={tab === 'live' ? 'LIVE' : null}/>
                ))}
              </div>
            ) : (
              <div className="card-moj p-10 text-center text-moj-charcoal/70">
                <p>{tab === 'live' ? 'No live stream right now — check the Upcoming or Previous tabs.' : tab === 'upcoming' ? 'No upcoming streams scheduled yet.' : 'No previous streams available.'}</p>
                <a href={`https://www.youtube.com/@${CHURCH.youtube.split('@').pop()}/streams`} target="_blank" rel="noopener noreferrer" className="btn-outline mt-4 inline-flex"><ExternalLink size={16}/> Browse on YouTube</a>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <aside className="space-y-4">
            <div className="card-moj p-5">
              <h3 className="font-serif text-lg text-moj-purple-deep mb-3">Service Times</h3>
              <ul className="text-sm space-y-1.5 text-moj-charcoal/80">
                <li><strong className="text-moj-purple">Sunday Worship</strong> — 10:30 – 12:30</li>
                <li><strong className="text-moj-purple">Prayer Cell</strong> — Wed 19:30</li>
                <li><strong className="text-moj-purple">Bible Study</strong> — Fri 19:30</li>
              </ul>
            </div>
            <div className="card-moj p-5">
              <h3 className="font-serif text-lg text-moj-purple-deep mb-3">Need Prayer?</h3>
              <p className="text-sm text-moj-charcoal/80 mb-3">Send us your prayer request and our team will pray with you.</p>
              <Link to="/contact" className="btn-primary text-sm w-full justify-center">Send Prayer Request</Link>
            </div>
            <div className="card-moj p-5">
              <h3 className="font-serif text-lg text-moj-purple-deep mb-3">Reach Us</h3>
              <ul className="text-xs space-y-2 text-moj-charcoal/80">
                <li className="flex gap-2"><MapPin size={14} className="text-moj-gold-deep mt-0.5"/> {CHURCH.address}</li>
                <li className="flex gap-2"><Phone size={14} className="text-moj-gold-deep"/> {CHURCH.phone}</li>
                <li className="flex gap-2"><Mail size={14} className="text-moj-gold-deep"/> {CHURCH.email}</li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
